import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Globe, Package, Users, FileText, TrendingUp, Clock, Calendar,
  ArrowUpRight, AlertCircle, CheckCircle, DollarSign, Plane,
  BarChart3, Activity, Eye, MessageCircle, RefreshCw
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import api from '../../lib/api';
import { formatDate } from '../../lib/utils';

const statusConfig = {
  initiated: { label: 'Initiée', color: 'bg-gray-500/20 text-gray-400' },
  received: { label: 'Reçue', color: 'bg-blue-500/20 text-blue-400' },
  launched: { label: 'Lancée', color: 'bg-yellow-500/20 text-yellow-400' },
  processed: { label: 'Traitée', color: 'bg-purple-500/20 text-purple-400' },
  completed: { label: 'Terminée', color: 'bg-green-500/20 text-green-400' }
};

export const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await api.get('/admin/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-sky-500 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Tableau de Bord</h2>
          <p className="text-gray-400">Vue d'ensemble de l'activité</p>
        </div>
        <Button onClick={fetchStats} variant="outline" className="border-white/10 text-gray-300">
          <RefreshCw className="w-4 h-4 mr-2" /> Actualiser
        </Button>
      </div>

      {/* Today's Highlight */}
      <div className="p-4 rounded-xl bg-gradient-to-r from-sky-500/10 to-purple-500/10 border border-white/10">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-sky-500/20 flex items-center justify-center">
            <Activity className="w-6 h-6 text-sky-400" />
          </div>
          <div>
            <p className="text-gray-400 text-sm">Aujourd'hui</p>
            <p className="text-white">
              <span className="text-2xl font-bold text-sky-400">{stats?.today_applications || 0}</span> nouvelles demandes
              <span className="mx-2 text-gray-500">•</span>
              <span className="text-2xl font-bold text-purple-400">{stats?.today_appointments || 0}</span> RDV
            </p>
          </div>
        </div>
      </div>

      {/* Main Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {/* Total Applications */}
        <Card className="bg-[#0F172A] border-white/10">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="w-10 h-10 rounded-lg bg-sky-500/20 flex items-center justify-center">
                <Globe className="w-5 h-5 text-sky-400" />
              </div>
              <Badge className="bg-sky-500/20 text-sky-400 text-[10px]">
                +{stats?.week_applications || 0} /sem
              </Badge>
            </div>
            <p className="text-2xl font-bold text-white">{stats?.total_applications || 0}</p>
            <p className="text-xs text-gray-400">Demandes Visa Total</p>
          </CardContent>
        </Card>

        {/* Pending */}
        <Card className="bg-[#0F172A] border-white/10">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="w-10 h-10 rounded-lg bg-yellow-500/20 flex items-center justify-center">
                <Clock className="w-5 h-5 text-yellow-400" />
              </div>
              <AlertCircle className="w-4 h-4 text-yellow-400" />
            </div>
            <p className="text-2xl font-bold text-white">{stats?.pending_applications || 0}</p>
            <p className="text-xs text-gray-400">En cours de traitement</p>
          </CardContent>
        </Card>

        {/* Completed */}
        <Card className="bg-[#0F172A] border-white/10">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-green-400" />
              </div>
              <TrendingUp className="w-4 h-4 text-green-400" />
            </div>
            <p className="text-2xl font-bold text-white">{stats?.completed_applications || 0}</p>
            <p className="text-xs text-gray-400">Dossiers terminés</p>
          </CardContent>
        </Card>

        {/* Revenue */}
        <Card className="bg-[#0F172A] border-white/10">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-emerald-400" />
              </div>
            </div>
            <p className="text-2xl font-bold text-white">${stats?.estimated_revenue || 0}</p>
            <p className="text-xs text-gray-400">Revenus estimés</p>
          </CardContent>
        </Card>
      </div>

      {/* Secondary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-[#0F172A] border-white/10">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
              <Calendar className="w-5 h-5 text-purple-400" />
            </div>
            <div>
              <p className="text-xl font-bold text-white">{stats?.total_appointments || 0}</p>
              <p className="text-xs text-gray-400">RDV Total</p>
            </div>
            {stats?.pending_appointments > 0 && (
              <Badge className="ml-auto bg-yellow-500/20 text-yellow-400 text-[10px]">
                {stats.pending_appointments} en attente
              </Badge>
            )}
          </CardContent>
        </Card>

        <Card className="bg-[#0F172A] border-white/10">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-orange-500/20 flex items-center justify-center">
              <Package className="w-5 h-5 text-orange-400" />
            </div>
            <div>
              <p className="text-xl font-bold text-white">{stats?.total_offers || 0}</p>
              <p className="text-xs text-gray-400">Offres actives</p>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-[#0F172A] border-white/10">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-pink-500/20 flex items-center justify-center">
              <Users className="w-5 h-5 text-pink-400" />
            </div>
            <div>
              <p className="text-xl font-bold text-white">{stats?.total_agents || 0}</p>
              <p className="text-xs text-gray-400">Agents</p>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-[#0F172A] border-white/10">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-cyan-500/20 flex items-center justify-center">
              <MessageCircle className="w-5 h-5 text-cyan-400" />
            </div>
            <div>
              <p className="text-xl font-bold text-white">{stats?.total_service_requests || 0}</p>
              <p className="text-xs text-gray-400">Demandes Services</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Status Breakdown */}
        <Card className="bg-[#0F172A] border-white/10">
          <CardHeader className="pb-2">
            <CardTitle className="text-white text-base flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-sky-400" />
              Répartition par Statut
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {stats?.status_counts && Object.entries(stats.status_counts).map(([status, count]) => {
                const total = stats.total_applications || 1;
                const percentage = Math.round((count / total) * 100);
                return (
                  <div key={status} className="space-y-1">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-400">{statusConfig[status]?.label || status}</span>
                      <span className="text-white font-medium">{count}</span>
                    </div>
                    <div className="h-2 bg-white/5 rounded-full overflow-hidden">
                      <div 
                        className={`h-full rounded-full ${statusConfig[status]?.color?.split(' ')[0] || 'bg-gray-500'}`}
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Top Destinations */}
        <Card className="bg-[#0F172A] border-white/10">
          <CardHeader className="pb-2">
            <CardTitle className="text-white text-base flex items-center gap-2">
              <Plane className="w-4 h-4 text-amber-400" />
              Top Destinations
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {stats?.destinations_breakdown?.length > 0 ? (
                stats.destinations_breakdown.map((dest, idx) => {
                  const maxCount = stats.destinations_breakdown[0]?.count || 1;
                  const percentage = Math.round((dest.count / maxCount) * 100);
                  const colors = ['bg-amber-500', 'bg-sky-500', 'bg-purple-500', 'bg-green-500', 'bg-pink-500'];
                  return (
                    <div key={idx} className="space-y-1">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-white">{dest.destination}</span>
                        <span className="text-gray-400">{dest.count} demandes</span>
                      </div>
                      <div className="h-2 bg-white/5 rounded-full overflow-hidden">
                        <div 
                          className={`h-full rounded-full ${colors[idx % colors.length]}`}
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                    </div>
                  );
                })
              ) : (
                <p className="text-gray-500 text-sm">Aucune donnée</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Applications */}
        <Card className="bg-[#0F172A] border-white/10">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-white text-base">Dernières Demandes Visa</CardTitle>
              <Link to="/admin/visa-applications" className="text-xs text-sky-400 hover:text-sky-300 flex items-center gap-1">
                Voir tout <ArrowUpRight className="w-3 h-3" />
              </Link>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {stats?.recent_applications?.length > 0 ? (
                stats.recent_applications.map((app, idx) => (
                  <div key={idx} className="flex items-center justify-between p-2 rounded-lg bg-white/5 hover:bg-white/10 transition-colors">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-sky-500/20 flex items-center justify-center">
                        <Globe className="w-4 h-4 text-sky-400" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-white">{app.applicant_name}</p>
                        <p className="text-xs text-gray-500">{app.destination}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <Badge className={`text-[10px] ${statusConfig[app.status]?.color}`}>
                        {statusConfig[app.status]?.label}
                      </Badge>
                      <p className="text-[10px] text-gray-500 mt-1">{formatDate(app.created_at)}</p>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-gray-500 text-sm text-center py-4">Aucune demande récente</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Recent Appointments */}
        <Card className="bg-[#0F172A] border-white/10">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-white text-base">Derniers Rendez-vous</CardTitle>
              <Link to="/admin/appointments" className="text-xs text-purple-400 hover:text-purple-300 flex items-center gap-1">
                Voir tout <ArrowUpRight className="w-3 h-3" />
              </Link>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {stats?.recent_appointments?.length > 0 ? (
                stats.recent_appointments.map((apt, idx) => (
                  <div key={idx} className="flex items-center justify-between p-2 rounded-lg bg-white/5 hover:bg-white/10 transition-colors">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-purple-500/20 flex items-center justify-center">
                        <Calendar className="w-4 h-4 text-purple-400" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-white">{apt.client_name}</p>
                        <p className="text-xs text-gray-500">{apt.visa_type}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-white">{apt.preferred_date}</p>
                      <p className="text-[10px] text-gray-500">{apt.preferred_time}</p>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-gray-500 text-sm text-center py-4">Aucun rendez-vous récent</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <Card className="bg-[#0F172A] border-white/10">
        <CardHeader className="pb-2">
          <CardTitle className="text-white text-base">Actions Rapides</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Link to="/admin/visa-applications" className="p-3 rounded-lg bg-sky-500/10 border border-sky-500/20 hover:bg-sky-500/20 transition-colors text-center">
              <Globe className="w-6 h-6 text-sky-400 mx-auto mb-2" />
              <p className="text-sm text-white">Gérer Visas</p>
            </Link>
            <Link to="/admin/appointments" className="p-3 rounded-lg bg-purple-500/10 border border-purple-500/20 hover:bg-purple-500/20 transition-colors text-center">
              <Calendar className="w-6 h-6 text-purple-400 mx-auto mb-2" />
              <p className="text-sm text-white">Gérer RDV</p>
            </Link>
            <Link to="/admin/offers" className="p-3 rounded-lg bg-orange-500/10 border border-orange-500/20 hover:bg-orange-500/20 transition-colors text-center">
              <Package className="w-6 h-6 text-orange-400 mx-auto mb-2" />
              <p className="text-sm text-white">Gérer Offres</p>
            </Link>
            <Link to="/admin/users" className="p-3 rounded-lg bg-pink-500/10 border border-pink-500/20 hover:bg-pink-500/20 transition-colors text-center">
              <Users className="w-6 h-6 text-pink-400 mx-auto mb-2" />
              <p className="text-sm text-white">Gérer Utilisateurs</p>
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminDashboard;
